// src/lib/auth.jsx
//
// PROVVISORIO: la Specifica v1.1 (§3, §4.1) prescrive di riusare login e
// gestione sessione già presenti nel frontend TalentGPT esistente. Qui
// forniamo un AuthProvider minimo con inserimento manuale del JWT e
// selezione del ruolo, utile per collaudare le differenze di ACL (§4.2)
// prima che il login reale sia collegato.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { KM_CONFIG } from '../api/config.js';
import { ROLES } from './roles.js';

const AuthContext = createContext(null);

const SESSION_KEY = 'talentgpt_km_jwt'; // sessionStorage, mai localStorage
const ROLE_KEY = 'talentgpt_km_role';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEY));
  // 28/09: per ora l'area Knowledge Management è allineata al ruolo ADMIN per
  // chiunque raggiunga la pagina (decisione di progetto): tutte le azioni
  // (apertura/modifica/chiusura esperienza, modifica e marcatura scheda) sono
  // disponibili. Il ruolo simulato in sessionStorage resta solo in MOCK_MODE.
  // Per tornare alla regola precedente: sessionStorage || (MOCK_MODE ? HR : null).
  const [role, setRoleState] = useState(
    () => (KM_CONFIG.MOCK_MODE && sessionStorage.getItem(ROLE_KEY)) || ROLES.ADMIN
  );

  const loginWithToken = useCallback((jwt, selectedRole) => {
    sessionStorage.setItem(SESSION_KEY, jwt);
    setToken(jwt);
    if (selectedRole) {
      sessionStorage.setItem(ROLE_KEY, selectedRole);
      setRoleState(selectedRole);
    }
  }, []);

  const setRole = useCallback((newRole) => {
    sessionStorage.setItem(ROLE_KEY, newRole);
    setRoleState(newRole);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    setToken(null);
    setRoleState(ROLES.ADMIN); // vedi nota sul ruolo ADMIN sopra
  }, []);

  const onUnauthorized = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      role,
      isAuthenticated: KM_CONFIG.MOCK_MODE || Boolean(token),
      loginWithToken,
      setRole,
      logout,
      onUnauthorized,
    }),
    [token, role, loginWithToken, setRole, logout, onUnauthorized]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro <AuthProvider>');
  return ctx;
}
